'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Wallet, Building2, CheckCircle2, Clock, XCircle, RefreshCw, ArrowLeft, Filter, Search, ArrowDownToLine,  } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DirectoryTopbar from '@/app/loan-aggregator-directory/components/DirectoryTopbar';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type PayoutMethod = 'bank_account' | 'upi';
type PayoutStatus = 'pending' | 'processing' | 'completed' | 'rejected';

interface PayoutRequest {
  id: string;
  amount: number;
  method: PayoutMethod;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string;
  upi_id: string;
  status: PayoutStatus;
  notes: string;
  requested_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatINR(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: PayoutStatus }) {
  const config: Record<PayoutStatus, { label: string; icon: React.ReactNode; cls: string }> = {
    pending: {
      label: 'Pending',
      icon: <Clock size={11} />,
      cls: 'bg-amber-50 text-amber-700 border-amber-200',
    },
    processing: {
      label: 'Processing',
      icon: <RefreshCw size={11} className="animate-spin" />,
      cls: 'bg-blue-50 text-blue-700 border-blue-200',
    },
    completed: {
      label: 'Completed',
      icon: <CheckCircle2 size={11} />,
      cls: 'bg-green-50 text-green-700 border-green-200',
    },
    rejected: {
      label: 'Rejected',
      icon: <XCircle size={11} />,
      cls: 'bg-red-50 text-red-600 border-red-200',
    },
  };
  const { label, icon, cls } = config[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}>
      {icon}
      {label}
    </span>
  );
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div className={`rounded-2xl border border-border bg-card p-4 flex flex-col gap-1 ${color}`}>
      <p className="text-xs text-muted-foreground font-medium">{label}</p>
      <p className="text-xl font-extrabold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}

// ─── Payout Row ───────────────────────────────────────────────────────────────

function PayoutRow({ tx }: { tx: PayoutRequest }) {
  const isUpi = tx.method === 'upi';
  const methodLabel = isUpi ? 'UPI Transfer' : 'Bank Transfer';
  const destination = isUpi
    ? tx.upi_id || '—'
    : tx.account_number
    ? `${tx.bank_name ? tx.bank_name + ' ••••' : '••••'}${tx.account_number.slice(-4)}`
    : '—';

  return (
    <div className="flex items-center gap-4 py-4 border-b border-border last:border-0 hover:bg-muted/30 transition-colors px-4 rounded-xl">
      {/* Method icon */}
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isUpi ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'
        }`}
      >
        {isUpi ? <Wallet size={18} /> : <Building2 size={18} />}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">{methodLabel}</p>
        <p className="text-xs text-muted-foreground truncate max-w-[180px]">{destination}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatDate(tx.requested_at)} · {formatTime(tx.requested_at)}
        </p>
      </div>

      {/* Amount + status */}
      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
        <p className="text-sm font-bold text-foreground">{formatINR(tx.amount)}</p>
        <StatusBadge status={tx.status} />
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyPayouts({ filtered }: { filtered: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
        <ArrowDownToLine size={24} className="text-muted-foreground" />
      </div>
      <p className="text-base font-semibold text-foreground mb-1">
        {filtered ? 'No matching requests' : 'No payout requests yet'}
      </p>
      <p className="text-sm text-muted-foreground max-w-xs">
        {filtered
          ? 'Try adjusting your filters or search query.' :'Once you submit a payout request, it will appear here.'}
      </p>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 py-4 border-b border-border px-4 animate-pulse">
      <div className="w-10 h-10 rounded-xl bg-muted flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 bg-muted rounded w-32" />
        <div className="h-3 bg-muted rounded w-48" />
        <div className="h-3 bg-muted rounded w-24" />
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="h-4 bg-muted rounded w-16" />
        <div className="h-5 bg-muted rounded-full w-20" />
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const STATUS_FILTERS: { label: string; value: PayoutStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Processing', value: 'processing' },
  { label: 'Completed', value: 'completed' },
  { label: 'Rejected', value: 'rejected' },
];

export default function PayoutHistoryPage() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();

  const [transactions, setTransactions] = useState<PayoutRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<PayoutStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Auth guard
  useEffect(() => {
    if (!authLoading && !user) router.push('/');
  }, [user, authLoading, router]);

  // Load payout requests
  const loadPayouts = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('payout_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('requested_at', { ascending: false });
      setTransactions(data ?? []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    if (user) loadPayouts();
  }, [user, loadPayouts]);

  // Derived stats
  const totalRequested = transactions.reduce((s, t) => s + Number(t.amount), 0);
  const totalCompleted = transactions
    .filter((t) => t.status === 'completed')
    .reduce((s, t) => s + Number(t.amount), 0);
  const pendingCount = transactions.filter((t) => t.status === 'pending' || t.status === 'processing').length;

  // Filtered list
  const filtered = transactions.filter((t) => {
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !q ||
      t.upi_id?.toLowerCase().includes(q) ||
      t.account_number?.includes(q) ||
      t.bank_name?.toLowerCase().includes(q) ||
      t.account_holder_name?.toLowerCase().includes(q) ||
      formatINR(t.amount).includes(q);
    return matchStatus && matchSearch;
  });

  return (
    <div className="min-h-screen bg-background">
      <DirectoryTopbar />

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => router.push('/payouts')}
            className="p-2 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Back to payouts"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-xl font-extrabold text-foreground">Payout History</h1>
            <p className="text-xs text-muted-foreground mt-0.5">All your withdrawal requests</p>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <SummaryCard
            label="Total Requested"
            value={formatINR(totalRequested)}
            sub={`${transactions.length} request${transactions.length !== 1 ? 's' : ''}`}
            color=""
          />
          <SummaryCard
            label="Total Paid Out"
            value={formatINR(totalCompleted)}
            sub="Completed"
            color=""
          />
          <SummaryCard
            label="In Progress"
            value={String(pendingCount)}
            sub="Pending / Processing"
            color=""
          />
        </div>

        {/* Search + Filter */}
        <div className="bg-card border border-border rounded-2xl p-4 mb-4 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by UPI ID, account, bank name…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm bg-muted/50 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
            />
          </div>

          {/* Status filter pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter size={13} className="text-muted-foreground flex-shrink-0" />
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  statusFilter === f.value
                    ? 'bg-primary text-white border-primary' :'bg-muted/50 text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                }`}
              >
                {f.label}
                {f.value !== 'all' && (
                  <span className="ml-1 opacity-70">
                    ({transactions.filter((t) => t.status === f.value).length})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Requests list */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {loading ? (
            <div className="divide-y divide-border">
              {[...Array(5)].map((_, i) => (
                <SkeletonRow key={i} />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyPayouts filtered={statusFilter !== 'all' || searchQuery.length > 0} />
          ) : (
            <div>
              {filtered.map((tx) => (
                <PayoutRow key={tx.id} tx={tx} />
              ))}
            </div>
          )}
        </div>

        {/* Footer note */}
        {!loading && filtered.length > 0 && (
          <p className="text-xs text-muted-foreground text-center mt-4">
            Showing {filtered.length} of {transactions.length} request{transactions.length !== 1 ? 's' : ''}
          </p>
        )}
      </main>
    </div>
  );
}
